interface MaskFunctions {
  CpfMask: (cpf: string) => string;
  RgMask: (rg: string) => string;
  BirthMask: (data: string) => string;
  CellMask: (cell: string) => string;
  PhoneMask: (phone: string) => string;
  Cell2Mask: (cell2: string) => string;
  PostalCodeMask: (postalCode: string) => string;
  DecimalMask: (value: string) => string;
  CilindricoMask: (value: string) => string;
  DecimalToNumber: (maskedValue: string) => number;
  NumberToDecimalMask: (numberValue: number | string | null | undefined) => string;
}

export default function Masks(): MaskFunctions {
  function CpfMask(cpf: string): string {
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/g, '$1.$2.$3-$4');
  }

  function RgMask(rg: string): string {
    return rg.replace(/(\d{1})(\d{3})(\d{3})/g, '$1.$2.$3');
  }

  function BirthMask(data: string): string {
    return data.replace(/(\d{2})(\d{2})(\d{4})/g, '$1/$2/$3');
  }

  function CellMask(cell: string): string {
    return cell.replace(/(\d{2})(\d{5})(\d{4})/g, '($1)$2-$3');
  }

  function PhoneMask(phone: string): string {
    return phone.replace(/(\d{2})(\d{4})(\d{4})/g, '($1)$2-$3');
  }

  function Cell2Mask(cell2: string): string {
    return cell2.replace(/(\d{2})(\d{5})(\d{4})/g, '($1)$2-$3');
  }

  function PostalCodeMask(postalCode: string): string {
    return postalCode.replace(/(\d{5})(\d{3})/g, '$1-$2');
  }

  function DecimalMask(value: string): string {
    // Check if it starts with minus or plus sign
    const isNegative = value.startsWith('-');
    const isExplicitPositive = value.startsWith('+');
    
    // Remove everything that's not a digit
    let digits = value.replace(/[^\d]/g, '');
    
    // If empty, return empty (with sign if typed)
    if (!digits) return isNegative ? '-' : isExplicitPositive ? '+' : '';
    
    // Convert to number and divide by 100 to get decimal value
    const numericValue = parseInt(digits) / 100;
    
    // Format with exactly 2 decimal places, using comma as decimal separator
    const formatted = numericValue.toFixed(2).replace('.', ',');
    
    // Add sign based on user input
    if (isNegative) return `-${formatted}`;
    if (isExplicitPositive) return `+${formatted}`;
    return formatted;
  }

  function CilindricoMask(value: string): string {
    // Check if it starts with plus sign (explicit positive)
    const isExplicitPositive = value.startsWith('+');
    const isExplicitNegative = value.startsWith('-');
    
    // Remove everything that's not a digit
    let digits = value.replace(/[^\d]/g, '');
    
    // If empty, return empty (with sign if typed)
    if (!digits) return isExplicitNegative ? '-' : isExplicitPositive ? '+' : '';
    
    // Convert to number and divide by 100 to get decimal value
    const numericValue = parseInt(digits) / 100;
    
    // Format with exactly 2 decimal places, using comma as decimal separator
    const formatted = numericValue.toFixed(2).replace('.', ',');
    
    // Logic: if user explicitly typed + keep positive, if typed - keep negative, otherwise default to negative
    if (isExplicitPositive) return `+${formatted}`;
    if (isExplicitNegative) return `-${formatted}`;
    
    // Default to negative for cilindrico (most common case)
    return `-${formatted}`;
  }

  function DecimalToNumber(maskedValue: string): number {
    if (!maskedValue || maskedValue === '-') return 0;
    
    // Replace comma with dot and convert to number
    return parseFloat(maskedValue.replace(',', '.'));
  }

  function NumberToDecimalMask(numberValue: number | string | null | undefined): string {
    if (numberValue === null || numberValue === undefined || numberValue === '') return '';
    
    const num = typeof numberValue === 'string' ? parseFloat(numberValue) : numberValue;
    if (isNaN(num)) return '';
    
    // Convert number to cents, then format as if user typed it
    const cents = Math.abs(Math.round(num * 100)).toString();
    const isNegative = num < 0;
    
    const numericValue = parseInt(cents) / 100;
    const formatted = numericValue.toFixed(2).replace('.', ',');
    
    return isNegative ? `-${formatted}` : formatted;
  }

  return {
    CpfMask,
    RgMask,
    BirthMask,
    CellMask,
    PhoneMask,
    Cell2Mask,
    PostalCodeMask,
    DecimalMask,
    CilindricoMask,
    DecimalToNumber,
    NumberToDecimalMask
  };
}
